// Global patient quick-panel state.
// Any component can call openPatient(id, name) — e.g. a clicked patient
// name on the schedule — and the slide-over panel (PatientQuickPanel,
// mounted once in App) shows that patient's full profile.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface PatientRef {
  id: number | string;
  name?: string;
}

interface PatientPanelValue {
  patient: PatientRef | null;
  openPatient: (id: number | string, name?: string) => void;
  closePatient: () => void;
}

export const PatientPanelContext = createContext<PatientPanelValue>({
  patient: null,
  openPatient: () => {},
  closePatient: () => {},
});

export const PatientPanelProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [patient, setPatient] = useState<PatientRef | null>(null);

  const openPatient = useCallback((id: number | string, name?: string) => {
    if (id === null || id === undefined || id === '') return;
    setPatient({ id, name });
  }, []);

  const closePatient = useCallback(() => setPatient(null), []);

  const value = useMemo(
    () => ({ patient, openPatient, closePatient }),
    [patient, openPatient, closePatient]
  );

  return (
    <PatientPanelContext.Provider value={value}>{children}</PatientPanelContext.Provider>
  );
};

export const usePatientPanel = () => useContext(PatientPanelContext);
